import type { z } from 'zod';
import type {
  AuditLogEntrySchema,
  AuditLogListEnvelope,
  AuditLogFacetsEnvelope,
} from '@contract/auditLogs';
import { apiClient } from './api';

/**
 * An organization's audit trail (REV-805). Owners and admins only; the API
 * answers anyone else 403 with code ROLE_REQUIRED.
 *
 * The types come from the vendored contract, which revenant-api checks against
 * entries written by its real routes.
 */
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;
export type AuditLogPage = z.infer<typeof AuditLogListEnvelope>['data'];
export type AuditLogFacets = z.infer<typeof AuditLogFacetsEnvelope>['data'];

/** The filters the list takes. Each is one exact value, or absent. */
export interface AuditLogFilters {
  action?: string;
  resourceType?: string;
  userId?: string;
}

export const auditLogService = {
  /** One page of entries, newest first. `page` counts from 1. */
  list: async (orgId: string, page: number, limit: number, filters: AuditLogFilters = {}) => {
    const params: Record<string, unknown> = { page, limit };
    for (const [key, value] of Object.entries(filters)) {
      if (value) params[key] = value;
    }
    const response = await apiClient.get<z.infer<typeof AuditLogListEnvelope>>(
      `/v1/organizations/${orgId}/audit-logs`,
      params
    );
    return response.data;
  },

  /**
   * The actions and resource types this organization's entries carry, for the
   * filters. Read from the entries, so every option matches something.
   */
  facets: async (orgId: string) => {
    const response = await apiClient.get<z.infer<typeof AuditLogFacetsEnvelope>>(
      `/v1/organizations/${orgId}/audit-logs/facets`
    );
    return response.data;
  },
};
